import React, { useState } from 'react'
import { authService } from './authService'
import { AlertCircle, CheckCircle2, ArrowLeft, Mail } from 'lucide-react'

interface ResetPasswordViewProps {
  onBackToLogin: () => void
}

export const ResetPasswordView: React.FC<ResetPasswordViewProps> = ({ onBackToLogin }) => {
  const [email, setEmail] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [errorMsg, setErrorMsg] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setErrorMsg(null)

    if (!email) {
      setErrorMsg('Introduce el correo electrónico asociado a tu cuenta.')
      return
    }

    setIsSubmitting(true)
    try {
      const { error } = await authService.requestPasswordReset(email)
      if (error) {
        setErrorMsg(error.message || 'No se pudo enviar el correo de recuperación.')
      } else {
        setSuccess(true)
      }
    } catch {
      setErrorMsg('Error de comunicación con el servicio de acceso.')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="auth-container">
      <div className="auth-card">
        <div className="auth-header">
          <img src="/branding/logo.png" alt="FEFA" className="auth-logo" />
          <h1 style={{ fontSize: '1.25rem', fontWeight: 700, color: 'var(--text-main)', marginBottom: '0.25rem' }}>
            Recuperar Contraseña
          </h1>
          <p style={{ fontSize: '0.82rem', color: 'var(--text-secondary)' }}>
            Te enviaremos un enlace seguro para restablecer tu acceso.
          </p>
        </div>

        {success ? (
          <div>
            <div
              style={{
                padding: '0.85rem',
                backgroundColor: 'var(--status-success-bg)',
                color: 'var(--status-success-text)',
                borderRadius: 'var(--radius-sm)',
                fontSize: '0.82rem',
                display: 'flex',
                alignItems: 'flex-start',
                gap: '0.5rem',
                marginBottom: '1.25rem',
              }}
            >
              <CheckCircle2 size={16} style={{ flexShrink: 0, marginTop: '2px' }} />
              <div>
                Si el correo <strong>{email}</strong> está registrado, recibirás un mensaje con las instrucciones de recuperación.
              </div>
            </div>

            <button
              type="button"
              className="btn btn-secondary"
              style={{ width: '100%' }}
              onClick={onBackToLogin}
            >
              <ArrowLeft size={16} />
              <span>Volver al inicio de sesión</span>
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            {errorMsg && (
              <div
                style={{
                  padding: '0.75rem',
                  backgroundColor: 'var(--status-danger-bg)',
                  color: 'var(--status-danger-text)',
                  border: '1px solid var(--status-danger-border)',
                  borderRadius: 'var(--radius-sm)',
                  fontSize: '0.82rem',
                  marginBottom: '1.25rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                }}
              >
                <AlertCircle size={16} style={{ flexShrink: 0 }} />
                <span>{errorMsg}</span>
              </div>
            )}

            <div className="form-group">
              <label className="form-label" htmlFor="reset-email">
                Correo electrónico registrado
              </label>
              <div style={{ position: 'relative' }}>
                <input
                  id="reset-email"
                  type="email"
                  className="form-input"
                  placeholder="tu.correo@fefa.es"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={isSubmitting}
                  autoComplete="email"
                  required
                />
                <Mail
                  size={16}
                  style={{
                    position: 'absolute',
                    right: '12px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    color: 'var(--text-muted)',
                  }}
                />
              </div>
            </div>

            <button
              type="submit"
              className="btn btn-primary"
              style={{ width: '100%', marginTop: '0.5rem' }}
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Enviando enlace...' : 'Enviar enlace de recuperación'}
            </button>

            <div style={{ marginTop: '1.25rem', textAlign: 'center' }}>
              <button
                type="button"
                onClick={onBackToLogin}
                style={{
                  fontSize: '0.8rem',
                  color: 'var(--text-secondary)',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.35rem',
                }}
              >
                <ArrowLeft size={14} />
                <span>Volver al inicio de sesión</span>
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  )
}
